import type { Key } from "./id";

/** English. Typed against the Indonesian keys: a missing string here fails the type check. */
export const en: Record<Key, string> = {
  // ---- common
  "common.all": "All",
  "common.cancel": "Cancel",
  "common.choose": "Choose…",
  "common.close": "Close",
  "common.edit": "Edit",
  "common.loading": "Loading…",
  "common.next": "Next",
  "common.prev": "Previous",
  "common.no": "No",
  "common.yes": "Yes",
  "common.range": "{from}–{to} of {total}",
  "common.remove": "Remove",
  "common.sample": "SAMPLE",
  "common.sample_hint": "Sample data, not a real unit",
  "common.save": "Save",
  "common.storeys": "{n} storey(s)",

  // ---- app, nav, roles
  "nav.label": "Main menu",
  "nav.dashboard": "Overview",
  "nav.factory": "Factory",
  "nav.units": "Units",
  "nav.types": "Types",
  "nav.handover": "Meda handover",
  "nav.growth": "Growth sync",
  "nav.audit": "Audit trail",
  "nav.settings": "Settings",
  "nav.logout": "Sign out",
  "role.admin": "Admin",
  "role.worker": "Factory worker",

  // ---- login and account
  "login.intro": "Sign in with your staff account.",
  "login.username": "Username",
  "login.password": "Password",
  "login.submit": "Sign in",
  "account.title": "My account",
  "account.must_change": "Your password is temporary. Set a new one to continue.",
  "account.current": "Current password",
  "account.new": "New password",
  "account.again": "Repeat new password",
  "account.rule": "At least 10 characters.",
  "account.mismatch": "The new passwords do not match.",
  "account.submit": "Change password",
  "account.changed": "Password changed.",

  // ---- statuses and timeline
  "status.issued": "ID issued",
  "status.label_printed": "Label printed",
  "status.label_fixed": "Label fixed",
  "status.shipped": "Shipped",
  "status.void": "Void",
  "tl.status.issued": "ID issued",
  "tl.status.label_printed": "Status: label printed",
  "tl.status.label_fixed": "Status: label fixed",
  "tl.status.shipped": "Unit shipped",
  "tl.status.void": "Unit voided",
  "tl.label.print": "Label printed",
  "tl.label.reprint": "Label reprinted",
  "tl.label.fix": "Label fixing confirmed",
  "tl.spec.type": "Type spec copied to the unit",
  "tl.spec.unit": "Spec deviation recorded",
  "tl.growth.factory": "Growth recorded (factory)",
  "tl.growth.dexss": "Growth recorded (Meda)",
  "tl.export": "Sent to Meda",
  "models.title": "3D model",
  "models.intro": "Upload the SketchUp export (OBJ or FBX) for this size. The engine automatically simplifies, compresses and checks it so it stays light on a phone. A model is only published if it fits the budget and has a preview picture.",
  "models.file": "Export file (a .zip with OBJ + textures, or .obj / .fbx / .glb)",
  "models.units": "Export units",
  "models.units_hint": "The units chosen when exporting from SketchUp. The wrong ones make the house look huge or tiny.",
  "models.up_axis": "Up axis",
  "models.upload": "Upload and process",
  "models.uploaded": "Model uploaded. The engine is processing it.",
  "models.none": "No 3D model for this size yet.",
  "models.version": "Version {n}",
  "models.published": "Published",
  "models.retired": "Withdrawn",
  "models.budget_ok": "Within budget",
  "models.budget_fail": "Too heavy",
  "models.processing": "Processing. This page updates by itself.",
  "models.metrics": "{tri} triangles · {mb} MB · {parts} parts · {tex} MB of textures in memory",
  "models.reduced": "Reduced from {from} triangles and {mb0} MB in the original file",
  "models.size": "model size {w} × {h} × {d} m",
  "models.too_heavy": "This model is too heavy for a smooth experience, so it cannot be published:",
  "models.poster_none": "No preview picture yet. Required before publishing: it shows instantly while the model loads.",
  "models.poster": "Preview picture",
  "models.preview": "View in 3D",
  "models.publish": "Publish",
  "models.retire": "Withdraw",
  "models.reprocess": "Process again",
  "models.reprocess_hint": "Uses the same file with other units or axis, without uploading it again.",
  "models.reprocessing": "Processing again.",
  "models.delete": "Delete",
  "models.delete_confirm": "Delete this model version? Its files are deleted too.",
  "models.published_ok": "Model published.",
  "models.retired_ok": "Model withdrawn.",
  "models.deleted_ok": "Model deleted.",
  "models.poster_saved": "Preview picture saved.",
  "models.poster_capture": "Use this view as the preview picture",
  "models.poster_upload": "Upload your own render",
  "models.poster_hint": "A render from Enscape, D5 or V-Ray usually looks best. Set the model's angle first, then capture it.",
  "settings.look.title": "Look of the rendered pictures",
  "settings.look.help": "One look is used for every rendered picture, so all models come out consistent. A change applies to pictures made from then on; pictures already made keep the look they were made with. To see the result, open a model that is not published yet and press 'Make the pictures again'.",
  "settings.look.size": "Picture size",
  "settings.look.size_hint": "Pixels. What is sent to apps is made smaller (at most 1600 px); a full-size copy is kept for print or a website.",
  "settings.look.quality": "Smoothness",
  "settings.look.quality_hint": "Smoother = less grain, but slower. How long it takes depends on the server's computer.",
  "settings.look.q.48": "Draft (fast, a bit grainy)",
  "settings.look.q.96": "Balanced (default)",
  "settings.look.q.192": "Smooth (about 2× slower)",
  "settings.look.q.384": "Very smooth (about 4× slower)",
  "settings.look.views": "Camera angles",
  "settings.look.views_hint": "Each angle becomes one picture. The first picture is also the preview picture. At most 6.",
  "settings.look.view_name": "Name",
  "settings.look.yaw": "Turn (°)",
  "settings.look.pitch": "Height (°)",
  "settings.look.add_view": "Add an angle",
  "settings.look.light": "Light",
  "settings.look.sun_elevation": "Sun height (°)",
  "settings.look.sun_elevation_hint": "Low = long, dramatic shadows; high = short shadows.",
  "settings.look.sun_azimuth": "Sun direction (°)",
  "settings.look.sun_azimuth_hint": "Where the light comes from. Change it if the front of the house is too dark.",
  "settings.look.sun_strength": "Sun strength",
  "settings.look.sun_strength_hint": "Stronger means more contrast between light and shadow.",
  "settings.look.sky_strength": "Sky light strength",
  "settings.look.sky_strength_hint": "Lights the sides that are in shadow.",
  "settings.look.exposure": "Exposure",
  "settings.look.exposure_hint": "Lower it if white walls look 'burnt'; raise it if everything is too dark.",
  "settings.look.colours": "Colours",
  "settings.look.backdrop_top": "Background (top)",
  "settings.look.backdrop_bottom": "Background (bottom)",
  "settings.look.floor": "Floor under the house",
  "settings.look.floor_hint": "Pick a colour that contrasts with the house walls.",
  "settings.look.reset": "Back to the default look",
  "settings.look.test": "Test this look",
  "settings.look.test_hint": "Draws one angle (the first) of one of your models with the look on screen, without saving. A rough, slightly grainy draft, but enough to judge the light and colours. About a minute.",
  "settings.look.test_failed": "The draft could not be made:",
  "settings.look.test_model": "Model drawn: {label}",
  "settings.look.test_draft": "A rough draft. The final pictures are smoother and full size.",
  "models.bundle": "Download bundle",
  "models.bundle_hint": "One .zip with the 3D model, full-size rendered pictures (JPEG), small pictures and an info file, for use outside Meda.",
  "models.renders.look": "Made with the look: {w}×{h} px, smoothness {q}",
  "models.renders.title": "Rendered pictures",
  "models.renders.hint": "Made automatically from the original SketchUp file, with real sunlight and shadows. Meda users see them instantly while the 3D model loads behind them. The first picture becomes the preview picture.",
  "models.renders.waiting": "Waiting for its turn. {n} renders are ahead; the renderer does one at a time.",
  "models.renders.loading": "The renderer is loading the model… (no first picture yet)",
  "models.renders.step": "Picture {k} of {n}: {view}",
  "models.renders.eta": "about {m} min left",
  "models.renders.eta_short": "less than a minute left",
  "models.renders.alive": "Running. Nothing is wrong while this bar moves; it is safe to leave.",
  "models.renders.failed": "The pictures could not be made:",
  "models.renders.again": "Make the pictures again",
  "models.renders.again_ok": "The pictures are being made again.",
  "models.renders.none": "No rendered pictures yet.",
  "models.renders.view.front": "Front",
  "models.renders.view.corner": "Corner",
  "models.renders.view.aerial": "From above",
  "models.perf": "Performance on this device",
  "models.perf_load": "Appeared in {s} s",
  "models.perf_fps": "≈ {fps} fps (lowest {min})",
  "models.perf_note": "Measured on the device and network you are using now. Also test on a mid-range Android phone.",
  "models.loading": "Loading the model…",
  "models.no_webgl": "This browser cannot show 3D models.",
  "models.status.queued": "Queued",
  "models.status.processing": "Processing…",
  "models.status.ready": "Ready",
  "models.status.failed": "Failed",
  "models.unit.m": "Metres (m)",
  "models.unit.cm": "Centimetres (cm)",
  "models.unit.mm": "Millimetres (mm)",
  "models.unit.in": "Inches (SketchUp default)",
  "models.unit.ft": "Feet (ft)",
  "models.up.y": "Y up (tick 'Swap YZ' when exporting)",
  "models.up.z": "Z up (CAD default)",

  // ---- dashboard
  "dash.title": "Overview",
  "dash.awaiting_label": "Waiting for a label",
  "dash.awaiting_confirmation": "Waiting for fixing confirmation",
  "dash.ready_to_ship": "Ready to ship",
  "dash.shipped": "Shipped",
  "dash.missing_spec": "Units without a spec",
  "dash.not_exported": "Not yet sent to Meda",
  "dash.reconcile": "Differences with Meda",
  "dash.flow_title": "Workflow",
  "dash.flow_intro": "The admin's steps from a new design to handing the data to Meda. The numbers show work waiting right now.",
  "dash.who.both": "Admin or factory worker",
  "dash.waiting": "{n} waiting",
  "dash.open": "Open",
  "dash.step.type": "Create the type and complete its spec",
  "dash.step.type_desc": "Once per design: growing scheme, sizes, modules, label position and the spec of each size. Every unit uses it.",
  "dash.step.issue": "Issue IDs",
  "dash.step.issue_desc": "Choose the type and size. Each unit gets a unique ID and automatically receives its type's spec.",
  "dash.step.print": "Print the QR label",
  "dash.step.print_desc": "The label holds the QR code and the ID. Printed from the Factory screen, any time after the ID is issued.",
  "dash.step.confirm": "Fix the label and confirm",
  "dash.step.confirm_desc": "The label is stuck on the unit, then fixing is confirmed.",
  "dash.step.ship": "Ship the unit",
  "dash.step.ship_desc": "Only once the label is confirmed fixed. The unit already has its type's spec.",
  "dash.step.handover": "Hand the data to Meda",
  "dash.step.handover_desc": "Units with a spec are sent to Meda.",
  "dash.placeholder_warn": "The activation link in the QR code is still a placeholder. Set the real Meda link before printing labels for real units.",

  // ---- factory screen
  "factory.title": "Factory: print and fix labels",
  "factory.find": "Type or scan the Netro ID",
  "factory.find_button": "Find",
  "factory.find_hint": "You can paste from a QR scanner. Dashes and letter case do not matter.",
  "factory.miss.bad_check": "The check character does not match, so a character was mistyped. Look at the ID on the label again.",
  "factory.miss.bad_format": "That ID format is not recognised. Check it again.",
  "factory.miss.not_found": "This ID does not exist in the system.",
  "factory.queue_print": "Waiting for a label",
  "factory.queue_confirm": "Printed, waiting for fixing confirmation",
  "factory.queue_empty": "No units.",
  "factory.void": "This unit is void. Do not use it.",
  "factory.done_fixed": "The label is fixed. The unit is ready to ship.",
  "factory.done_shipped": "This unit has already shipped.",
  "factory.open_details": "Open unit details",

  // ---- units list and issue dialog
  "units.title": "Units",
  "units.issue": "Issue IDs",
  "units.search": "Search ID",
  "units.status": "Status",
  "units.type": "Type",
  "units.only_missing_spec": "Only those without a spec",
  "units.empty": "No matching units.",
  "issue.type": "Type",
  "issue.size": "Size",
  "issue.size_hint": "The size when the unit leaves the factory. It goes into the ID and never changes.",
  "issue.quantity": "Number of units",
  "issue.production_date": "Production date",
  "issue.date_hint": "The year and month become part of the ID.",
  "issue.batch": "Batch (optional)",
  "issue.batch_hint": "A new code is created automatically.",
  "issue.sample": "Mark as sample/test data",
  "issue.submit": "Issue {n} ID(s)",
  "issue.done": "{n} ID(s) issued.",
  "issue.more": "and {n} more.",

  // ---- unit detail
  "unit.id": "Netro ID",
  "unit.type": "Type",
  "unit.client": "Client",
  "unit.size": "Size",
  "unit.size_at_production": "Size at production",
  "unit.current_size": "Current size",
  "unit.made_as": "made as {n} m²",
  "unit.batch": "Batch",
  "unit.production_date": "Production date",
  "unit.shipping_date": "Shipping date",
  "unit.id_format": "ID format version",
  "unit.late_issue": "ID issued after production",
  "unit.grown_note": "This unit has grown. The ID stays the same, and the size segment of the ID is always the size at production.",
  "unit.spec": "Spec",
  "unit.exported": "To Meda",
  "unit.identity": "Identity",
  "unit.label": "QR label",
  "unit.placement": "Label position",
  "unit.placement_none": "No label position note for this type yet.",
  "unit.checklist": "Completeness",
  "unit.check.label": "Label fixed",
  "unit.check.spec": "Spec",
  "unit.check.shipped": "Unit shipped",
  "unit.check.exported": "Sent to Meda",
  "unit.tab.overview": "Overview",
  "unit.tab.spec": "Spec",
  "unit.tab.growth": "Growth",
  "unit.tab.history": "History",
  "unit.tab.handover": "Handover",
  "unit.not_exported": "This unit has not been sent to Meda yet.",
  "unit.ship": "Ship unit",
  "unit.ship_no_spec": "A unit must not leave the factory without a spec. The spec is entered once on the type and used by every unit; an older unit can apply it in the Spec tab.",
  "unit.shipped_ok": "Unit marked as shipped.",
  "unit.void": "Void ID",
  "unit.void_banner": "This ID is void and will never be used again.",
  "unit.void_reason": "Reason for voiding",
  "unit.void_warn": "A voided ID is never deleted or reused. This is only possible before the label is fixed.",
  "unit.voided_ok": "ID voided.",

  // ---- label
  "label.print": "Print label",
  "label.reprint": "Reprint",
  "label.confirm": "Label is fixed",
  "label.confirm_hint": "Press this once the label is really stuck on the unit. The unit cannot ship before that.",
  "label.reason": "Reason for reprint",
  "label.reason_hint": "For example: damaged, scratched, lost.",
  "label.copies": "Copies",
  "label.reprint_warn": "The fixed label will count as unconfirmed until the new one is fixed and confirmed.",
  "label.preview": "Label preview",
  "label.printed_ok": "Label created. Print the PDF that opened.",
  "label.confirmed_ok": "Label fixing confirmed.",
  "label.print_count": "Printed {n} time(s)",
  "label.fixed_at": "fixed {when}",
  "label.qr_contains": "What the QR code contains",

  // ---- spec
  "spec.title": "Unit spec",
  "spec.version": "Version",
  "spec.latest": "Latest",
  "spec.from_template": "This unit has no spec yet (it was issued before this rule). The values below are its type's spec for this size. Apply it to this unit with one click.",
  "spec.viewing_old": "Viewing version {n} (read only).",
  "spec.recorded": "Version {v} recorded by {who} on {when}",
  "spec.required": "Required",
  "spec.required_progress": "Required fields filled: {filled} of {total}.",
  "spec.inherited": "This spec was copied from the type when the ID was issued. Every unit of this type and size is the same, so nothing is entered per unit.",
  "spec.deviation_banner": "This unit deviates from its type's spec. Version 1 is the type's spec.",
  "spec.deviation_warn": "You are recording a deviation from the type's spec for this one unit. Use it only when this unit really differs, for example a buyer's special option.",
  "spec.record_deviation": "Record a deviation",
  "spec.reason": "Reason (required)",
  "spec.reason_hint": "For example: buyer's special option, material substitution.",
  "spec.save_deviation": "Save deviation",
  "spec.source.type": "from the type",
  "spec.source.unit": "deviation",
  "spec.source.manual": "entered manually",
  "spec.add_row": "Add row",
  "spec.row": "Row {n}",
  "spec.rows_empty": "No rows yet.",
  "spec.openings_summary": "Opening area (calculated from the table)",
  "spec.openings_total": "Total",
  "spec.confirm_save": "Apply the type's spec to this unit",
  "spec.saved": "Specification saved.",
  "equip.add_room": "Add room",
  "equip.add_item": "Add item",
  "equip.other_room": "Other room…",
  "equip.other_item": "Other item…",
  "equip.room_name": "Room name",
  "equip.item_name": "Item name",
  "equip.remove_room": "Remove room",
  "equip.empty": "No rooms yet.",
  "excel.import": "Import from Excel",
  "excel.export": "Download Excel",
  "excel.help": "Download the Excel sheet, fill it in Excel, then import it again. Import only fills the form: check it, then Save.",
  "excel.downloaded": "Excel downloaded.",
  "excel.imported": "Filled from Excel: {fields} fields, {rows} rows. Check it, then Save.",
  "excel.nothing": "Nothing to fill could be read from this file.",
  "excel.warn_value": "Field {field} left out: {detail}",
  "excel.warn_row": "{field}, row {row} left out: {detail}",
  "group.geometry": "Geometry",
  "group.envelope": "Envelope",
  "group.energy": "Energy",
  "group.systems": "Building systems",
  "group.quality": "Quality references",
  "ftype.number": "Number",
  "ftype.text": "Text",
  "ftype.choice": "Choice",
  "ftype.boolean": "Yes/No",
  "ftype.date": "Date",
  "ftype.url": "Link",
  "ftype.table": "Table",
  "opt.window": "Window",
  "opt.door": "Door",
  "opt.1": "Floor 1",
  "opt.2": "Floor 2",
  "opt.3": "Floor 3",
  "opt.all": "All floors",
  "opt.front": "Front",
  "opt.back": "Back",
  "opt.left": "Left",
  "opt.right": "Right",
  "opt.wall": "Wall",
  "opt.floor": "Floor",
  "opt.roof": "Roof",

  // ---- growth
  "growth.scheme": "Growing scheme",
  "growth.legend_start": "size at production",
  "growth.legend_current": "thick outline: current size",
  "growth.records": "Growth records",
  "growth.add": "Record growth",
  "growth.edit": "Edit growth record",
  "growth.at_end": "This unit is at the last step of its scheme; there is no larger size.",
  "growth.none": "No growth recorded yet.",
  "growth.date": "Date",
  "growth.date_hint": "Cannot be before the production date.",
  "growth.change": "Change",
  "growth.modules": "Modules",
  "growth.modules_hint": "Prefilled from the difference in module composition between the two sizes. You can change it.",
  "growth.no_catalogue": "No modules in the catalogue yet (Settings → Modules).",
  "growth.source": "Source",
  "growth.src_factory": "Factory",
  "growth.state": "Status",
  "growth.current": "Current",
  "growth.void": "Void",
  "growth.from": "From size",
  "growth.to": "To size",
  "growth.note": "Note",
  "growth.saved": "Growth saved.",
  "growth.voided": "Growth record voided.",
  "growth.void_action": "Void record",
  "growth.void_reason": "Reason",
  "growth.void_warn": "The record is not deleted: a void version is added and the unit's size goes back to the previous record.",
  "growth.rules": "A unit can only grow to a later step of its own type's growing scheme. Only the latest record can be edited or voided, and a change creates a new version.",

  // ---- growth sync
  "growthsync.title": "Growth sync with Meda",
  "growthsync.how": "Growth is recorded in two systems: here from the factory side (modules produced), and in Meda from the owner side. Neither overwrites the other: differences are listed below for an admin to resolve.",
  "growthsync.import": "Import records from Meda",
  "growthsync.import_text": "Choose the JSON file of growth records from Meda. Importing the same file twice is safe.",
  "growthsync.choose_file": "Choose JSON file",
  "growthsync.format": "File format",
  "growthsync.imported": "{n} new record(s) imported.",
  "growthsync.result": "Received {received}, new {created}, already known {dup}, errors {err}.",
  "growthsync.reconcile": "Differences to check",
  "growthsync.reconcile_text": "Units whose size differs between the factory and Meda.",
  "growthsync.in_sync": "No differences. The factory and Meda agree.",
  "growthsync.factory": "Factory",
  "growthsync.issue": "Issue",
  "growthsync.factory_missing": "Meda recorded a larger size; the factory has not",
  "growthsync.size_mismatch": "Sizes differ",

  // ---- handover
  "handover.title": "Handover to Meda",
  "handover.tab.export": "Create a file",
  "handover.tab.log": "Export log",
  "handover.what": "The file holds, per unit: the ID, the type with its growing scheme, the spec, production and shipping dates, growth records, and the label's activation link. JSON is the complete format; CSV is only a flat summary.",
  "handover.choose": "Units to send",
  "handover.by_units": "Pick units",
  "handover.by_batch": "One batch",
  "handover.batch_hint": "Voided units are skipped.",
  "handover.include_sent": "Also show units already sent",
  "handover.none_to_send": "No units to send.",
  "handover.selected": "{n} unit(s) selected",
  "handover.n_units": "{n} unit(s)",
  "handover.check": "Check",
  "handover.pick_first": "Pick units or a batch first.",
  "handover.missing_spec": "These units have no spec yet. Apply their type's spec first:",
  "handover.has_void": "The selection contains voided units. Remove them.",
  "handover.already": "{n} unit(s) were sent before. Sending them again must be confirmed.",
  "handover.ready": "All units are ready to send.",
  "handover.format": "Format",
  "handover.format_json": "complete",
  "handover.format_csv": "flat summary",
  "handover.export": "Create and download file",
  "handover.done": "File {name} created and logged.",
  "handover.again_title": "Send again?",
  "handover.again_text": "{n} unit(s) were already handed to Meda. The new file will be logged as a re-export.",
  "handover.again_confirm": "Yes, send again",
  "handover.log_empty": "Nothing has been sent yet.",
  "handover.when": "When",
  "handover.who": "By",
  "handover.file": "File",
  "handover.units": "Units",
  "handover.checksum": "SHA-256",
  "handover.reexport": "re-export",
  "handover.download": "Download",
  "handover.sync.title": "Sync with Meda",
  "handover.sync.what": "Send every type and every unit that has a spec to Meda in one go, with one click. Safe to repeat: Meda only updates what it already has.",
  "handover.sync.button": "Sync now",
  "handover.sync.target": "Target: {target}",
  "handover.sync.off": "The connection to Meda is not set up. Set Meda_SYNC_URL and Meda_SYNC_TOKEN in the .env file, then restart the app.",
  "handover.sync.never": "Never synced yet.",
  "handover.sync.last_ok": "Last synced {when} by {who}: {types} type(s), {units} unit(s).",
  "handover.sync.last_fail": "The last attempt on {when} failed. {error}",
  "handover.sync.done": "Done: {types} type(s) and {units} unit(s) sent.",
  "handover.sync.skipped": "{n} unit(s) were left out because they have no spec yet: {ids}",
  "handover.sync.badge": "sync",
  "handover.sync.badge_failed": "failed",

  // ---- types
  "types.title": "Netro types",
  "types.intro": "A type is one design. Every design change is a new type. The client/developer is stored only on the type, never in the ID.",
  "types.new": "New type",
  "types.empty": "No types yet.",
  "types.code": "Type code",
  "types.code_hint": "Exactly the length set in the ID format, letters A–Z or digits. Cannot change once units exist.",
  "types.name": "Name",
  "types.description": "Description",
  "types.client": "Client/developer",
  "types.client_hint": "Leave empty for a general type.",
  "types.new_client": "New client",
  "types.new_client_hint": "The client is selected for this type as soon as it is saved.",
  "types.client_added": "Added and selected: {name}",
  "types.general": "General type",
  "types.units": "Units",
  "types.scheme": "Growing scheme",
  "types.scheme_help": "The ordered sizes a unit of this type can reach. Each step is also a size the factory can produce. A size cannot appear twice in one type.",
  "types.retired": "retired",
  "types.header": "Type details",
  "types.placement": "Label position on the unit",
  "types.placement_hint": "Shown on the factory screen so labels always go in the same place.",
  "types.active": "Active (new IDs may be issued)",
  "types.is_sample": "Sample data",
  "types.add_step": "Add step",
  "types.step": "Step {n}",
  "types.step_base": "Base type",
  "types.size": "Size",
  "types.storeys": "Storeys",
  "types.producible": "Can be produced at the factory",
  "types.modules": "Modules in this step",
  "types.add_module": "Add module",
  "types.quantity": "Quantity",
  "types.modules_hint": "The unit's module composition at this size.",
  "types.template": "Spec for this size",
  "types.template_hint": "Enter it once, here. Every unit of this size uses it automatically, so nothing is typed again per unit. New IDs cannot be issued until the required fields are filled in.",
  "types.spec_complete": "Complete",
  "types.spec_incomplete": "{n} required field(s) empty",
  "types.spec_applies_new": "Units already issued do not change: edits only apply to units issued afterwards.",
  "types.move_up": "Move up",
  "types.move_down": "Move down",
  "types.create": "Create type",
  "types.created": "Type created.",
  "types.saved": "Type saved.",
  "types.locked": "{n} unit(s) have been issued for this type, so the code, sizes, order and storeys are locked. A design change means a new type. Names, notes, modules, templates and new steps at the end can still be edited.",

  // ---- audit
  "audit.title": "Audit trail",
  "audit.intro": "Every creation, change, reprint, growth and export is recorded with the user and time. These records cannot be edited or deleted, not even by the database.",
  "audit.entity": "ID / code",
  "audit.user": "User",
  "audit.action": "Action",
  "audit.when": "When",
  "audit.what": "Description",
  "audit.details": "Show before/after",
  "audit.before": "Before",
  "audit.after": "After",
  "audit.empty": "No records.",

  // ---- settings
  "settings.saved": "Saved.",
  "settings.active": "Active",
  "settings.tab.idformat": "ID format",
  "settings.tab.label": "Label",
  "settings.tab.activation": "Activation link",
  "settings.tab.models3d": "3D models",
  "settings.models.title": "3D model budget",
  "settings.models.help": "The limits a model must stay inside to feel smooth on a phone. This is a starting point: adjust it after trying a mid-range Android phone. A model over the limits cannot be published.",
  "settings.models.triangles": "Maximum triangles",
  "settings.models.triangles_hint": "More = more detail but heavier.",
  "settings.models.size": "Maximum file size",
  "settings.models.size_hint": "What the user downloads over mobile data.",
  "settings.models.texture": "Maximum texture size",
  "settings.models.texture_hint": "Textures larger than this are shrunk automatically.",
  "settings.models.parts": "Maximum parts",
  "settings.models.parts_hint": "Too many separate parts make a phone stutter.",
  "settings.models.memory": "Maximum texture memory",
  "settings.models.memory_hint": "Graphics memory the textures use. Cheap phones run out here first.",
  "settings.models.encoding_title": "File format",
  "settings.models.encoding": "Compression",
  "settings.models.encoding_efficient": "Efficient (smallest and fastest)",
  "settings.models.encoding_compatible": "Compatible (opens in any 3D viewer)",
  "settings.models.encoding_efficient_hint": "Much smaller files and lighter on memory, but the viewer must support WebP, mesh quantization and meshopt. The viewer in this app does; the Meda team needs to confirm theirs does.",
  "settings.models.encoding_compatible_hint": "No special compression: opens in any glTF viewer with no extra decoder, but the files are bigger and may exceed the size limit more easily.",
  "settings.models.base_title": "File address for Meda",
  "settings.models.base": "Public address of this server",
  "settings.models.base_hint": "The prefix Meda uses to fetch models, for example this server's address or a CDN in front of it. Leave empty for a relative address.",
  "settings.tab.fields": "Spec fields",
  "settings.tab.modules": "Modules",
  "settings.tab.clients": "Clients",
  "settings.tab.batches": "Batches",
  "settings.tab.users": "Users",
  "settings.id.title": "Netro ID format",
  "settings.id.help": "Current format is version {v}. Changes apply to new IDs only; units already issued keep their format and old labels stay valid. Type codes must be as long as this setting.",
  "settings.id.prefix": "Prefix",
  "settings.id.type_len": "Type code length",
  "settings.id.size_len": "Size digits (m²)",
  "settings.id.period": "Year-month format",
  "settings.id.seq_len": "Sequence digits",
  "settings.id.separator": "Separator",
  "settings.id.check": "Check character",
  "settings.id.check_hint": "Catches a single mistyped character and two swapped neighbours when an ID is typed by hand.",
  "settings.id.none": "none",
  "settings.id.example": "Example ID",
  "settings.id.length": "{n} characters",
  "settings.label.title": "Label size and print type",
  "settings.label.help": "The label size and printer type are not decided yet. Set them here: the PDF follows these settings.",
  "settings.label.mode": "Print mode",
  "settings.label.sheet": "A4 sheet (ordinary printer)",
  "settings.label.single": "One label per page (label printer)",
  "settings.label.mode_sheet": "Labels are laid out in a grid on A4 paper.",
  "settings.label.mode_single": "The PDF page size equals the label size.",
  "settings.label.width": "Label width",
  "settings.label.height": "Label height",
  "settings.label.margin": "Paper margin",
  "settings.label.gap": "Gap between labels",
  "settings.label.ec": "QR error correction",
  "settings.label.ec_hint": "Higher = still readable when scratched, but a denser code.",
  "settings.label.guides": "Dotted cutting guides",
  "settings.label.preview": "Preview",
  "settings.label.preview_hint": "The preview uses the latest unit and the saved settings.",
  "settings.label.no_unit": "Issue an ID to see a preview.",
  "settings.label.too_small": "The label is too small for a scannable QR code. Make the label bigger.",
  "settings.act.title": "Meda activation link",
  "settings.act.help": "The QR holds one link: this address followed by the Netro ID. The field technician scans it with the Meda app to pair the Smartbox.",
  "settings.act.base": "Base address",
  "settings.act.hint": "Without the ID at the end, e.g. https://…/activate",
  "settings.act.example": "Example QR content",
  "settings.act.placeholder": "This address is still an unreachable placeholder. Set the real Meda address before printing labels for real units.",
  "settings.fields.help": "The spec field list is closed and extended by configuration. A field's key, group and type cannot change once created because older specs depend on them. Fields that are no longer used are deactivated, not deleted.",
  "settings.fields.add": "Add field",
  "settings.fields.label": "Label",
  "settings.fields.key": "Key",
  "settings.fields.key_hint": "lowercase letters, digits, underscore",
  "settings.fields.fixed": "Cannot be changed",
  "settings.fields.group": "Group",
  "settings.fields.type": "Type",
  "settings.fields.unit": "Unit",
  "settings.fields.label_id": "Label (Bahasa Indonesia)",
  "settings.fields.label_en": "Label (English)",
  "settings.fields.choices": "Choices",
  "settings.fields.choices_hint": "Separate with commas",
  "settings.fields.required": "Required (counts toward completeness)",
  "settings.fields.table_note": "This is a table. Its columns are fixed by the system; only the labels, required flag and active status can be changed here.",
  "settings.modules.help": "The module catalogue. Each type step's module composition and growth records refer to it.",
  "settings.modules.add": "Add module",
  "settings.modules.empty": "No modules yet.",
  "settings.modules.code_fixed": "The code cannot change. Retire the module and create a new one.",
  "settings.clients.help": "Clients/developers chosen on the type record. A client name never goes into the ID.",
  "settings.clients.add": "Add client",
  "settings.clients.name": "Client name",
  "settings.clients.empty": "No clients yet.",
  "settings.batches.help": "A batch groups units produced together. Batches are also created automatically when issuing IDs.",
  "settings.batches.add": "Add batch",
  "settings.batches.code": "Batch code",
  "settings.batches.code_hint": "Letters, digits, dot, dash, slash.",
  "settings.batches.note": "Note",
  "settings.batches.empty": "No batches yet.",
  "settings.users.help": "Two roles: admin (all data) and factory worker (print labels and confirm fixing).",
  "settings.users.add": "Add user",
  "settings.users.name": "Full name",
  "settings.users.role": "Role",
  "settings.users.last_login": "Last sign-in",
  "settings.users.you": "You",
  "settings.users.disabled": "disabled",
  "settings.users.reset": "Reset password",
  "settings.users.reset_hint": "Leave empty to keep it. The user will be asked to choose their own at next sign-in.",
  "settings.users.temp_hint": "A temporary password, at least 10 characters. The user changes it at first sign-in.",
  "settings.users.username_hint": "lowercase letters, digits, dot, dash",

  // ---- errors (codes from the API)
  "err.unknown": "Something went wrong. Try again.",
  "err.network": "Cannot reach the server.",
  "err.validation": "Some input is not valid. Check it again.",
  "err.conflict": "That change conflicts with another one. Reload and try again.",
  "err.csrf": "Request refused. Reload the page.",
  "err.not_authenticated": "Please sign in.",
  "err.session_expired": "Your session expired. Please sign in again.",
  "err.account_disabled": "This account is disabled.",
  "err.account_locked": "Too many failed attempts. Try again in a few minutes.",
  "err.invalid_credentials": "Wrong username or password.",
  "err.wrong_password": "The current password is wrong.",
  "err.password_too_short": "The password must be at least {n} characters.",
  "err.forbidden": "This action is for admins only.",
  "err.username_taken": "That username is already used.",
  "err.user_not_found": "User not found.",
  "err.last_admin": "There must always be at least one active admin.",
  "err.id_format_missing": "No ID format is configured.",
  "err.invalid_id_format": "That ID format is not valid.",
  "err.client_exists": "A client with that name already exists.",
  "err.client_not_found": "Client not found.",
  "err.module_code_taken": "That module code is already used.",
  "err.module_not_found": "Module not found.",
  "err.module_code_fixed": "A module code cannot be changed.",
  "err.duplicate_module": "A module can appear only once.",
  "err.spec_key_taken": "That field key is already used.",
  "err.choices_required": "A choice field needs at least one choice.",
  "err.choices_not_allowed": "Only choice fields have choices.",
  "err.spec_field_not_found": "Field not found.",
  "err.batch_exists": "A batch with that code already exists.",
  "err.type_not_found": "Type not found.",
  "err.invalid_type_code": "The type code must be exactly {n} characters, letters A–Z or digits.",
  "err.type_code_taken": "That type code is already used.",
  "err.duplicate_size": "A size can appear only once in a scheme.",
  "err.size_too_large": "That size is too large for the ID format.",
  "err.no_producible_step": "At least one step must be producible.",
  "err.step_not_found": "Step not found.",
  "err.duplicate_step": "A step appears twice.",
  "err.type_locked": "Units already exist for this type. Its code, sizes, order and storeys cannot change; a design change means a new type.",
  "err.type_inactive": "This type has been retired.",
  "err.type_code_not_valid_for_format": "The type code does not fit the current ID format.",
  "err.step_not_in_scheme": "That size is not part of this type's scheme.",
  "err.step_not_producible": "This size cannot be produced at the factory.",
  "err.production_date_out_of_range": "The production date is too far from today. Check the year and month, because they are part of the ID.",
  "err.sequence_exhausted": "Not enough sequence numbers left this month for that type.",
  "err.unit_not_found": "Unit not found.",
  "err.unit_void": "A voided unit cannot be changed.",
  "err.reprint_reason_required": "Say why the label is being reprinted.",
  "err.label_not_printed": "Print the label first, then confirm it is fixed.",
  "err.label_not_fixed": "The label must be confirmed fixed before the unit ships.",
  "err.spec_missing": "The unit has no spec yet. Complete the type's spec and apply it before the unit ships.",
  "err.shipping_before_production": "Shipping cannot be dated before production.",
  "err.cannot_void": "Only units whose label is not yet fixed can be voided.",
  "err.label_too_small": "The label is too small for a scannable QR code. Make it bigger in Settings.",
  "err.label_does_not_fit_page": "That label size does not fit on an A4 page.",
  "err.nothing_to_print": "No labels to print.",
  "err.invalid_spec_value": "Some specification input is not valid.",
  "err.spec_reason_required": "Say why this unit differs from the type's spec.",
  "err.type_spec_incomplete": "Complete the spec of the {n} m² size on the type first (Types → size → Spec for this size).",
  "err.unknown_spec_field": "Some specification field is not recognised.",
  "err.excel_not_xlsx": "This is not an Excel (.xlsx) file.",
  "err.excel_too_large": "The file is too large (2 MB at most).",
  "err.excel_unreadable": "The Excel file could not be read.",
  "err.spec_exists": "This unit already has a spec.",
  "err.spec_unchanged": "Nothing changed since the last version.",
  "err.spec_version_not_found": "Spec version not found.",
  "err.growth_not_forward": "A unit can only grow to a later step of its type's scheme.",
  "err.growth_before_production": "Growth cannot be dated before production.",
  "err.date_out_of_range": "That date is too far in the future.",
  "err.growth_not_found": "Growth record not found.",
  "err.growth_voided": "This record has been voided.",
  "err.growth_not_latest": "Only the latest growth record can be edited or voided.",
  "err.invalid_import": "The import file is not valid.",
  "err.export_scope": "Choose units or one batch.",
  "err.export_has_void": "Voided units cannot be sent.",
  "err.export_missing_spec": "These units have no spec yet. Apply their type's spec before sending.",
  "err.already_exported": "These units were already sent to Meda.",
  "err.nothing_to_export": "There are no units to send.",
  "err.export_not_found": "Export not found.",
  "err.dexss_not_configured": "The connection to Meda has not been set up.",
  "err.dexss_unreachable": "Meda could not be reached. Check that it is running and that the address is right.",
  "err.dexss_rejected": "Meda did not accept the data. Check the token, and that both sides use the same file version.",
  "err.dexss_error": "Meda had a problem receiving the data. Try again in a moment.",
  "err.upload_too_large": "The file is too large.",
  "err.unsupported_model_file": "Upload a .zip, .obj, .fbx or .glb file.",
  "err.bad_zip": "That file is not a valid zip.",
  "err.unsafe_zip": "The zip was refused because its contents are unsafe or too large.",
  "err.no_model_in_zip": "The zip has no .obj, .fbx or .glb file.",
  "err.ambiguous_model": "The zip has more than one model. Zip one model only.",
  "err.model_not_found": "3D model not found.",
  "err.model_not_ready": "The model is not ready yet.",
  "err.model_published": "A published model cannot be changed. Upload a new version.",
  "err.model_busy": "The model is still being processed.",
  "err.model_not_published": "This model is not the published one.",
  "err.over_budget": "This model is too heavy for a smooth experience. Export a lighter one or change the budget in Settings.",
  "err.poster_required": "Set a preview picture first: it shows instantly while the model loads.",
  "err.unsupported_image": "The picture must be a PNG, JPEG or WebP image.",
  "err.poster_failed": "The preview picture could not be processed.",
  "err.poster_missing": "No preview picture yet.",
  "err.look_test_busy": "Another draft is still being made. Wait a moment.",
  "err.no_model_for_test": "Upload a 3D model first. The look test draws one of your existing models.",
  "err.render_missing": "That picture was not found.",
  "err.no_test_picture": "No test picture yet.",
};
